import React, { useState, useEffect } from "react";
import _ from "lodash";
import { Grid, Typography, Card, CardContent, Container } from "@mui/material";
import ErrorBoundary from "../Common/ErrorBoundary";
import AnalyticsService from "../../Services/AnalyticsService";
import SectionTitle from "../Common/SectionTitle";
import Slider from "@mui/material/Slider";
import Skeleton from "@mui/material/Skeleton";
import ProductService from "../../Services/ProductService";
import PartnerService from "../../Services/PartnerService";

const DepreciatingCashDiscountBenefitContainer = (props) => {
  let [discountBenefits, setDiscountBenefits] = useState(null);
  const [modifiedDiscountBenefits, setModifiedDiscountBenefits] =
    useState(null);
  let [sliderMarks, setSliderMarks] = useState(null);
  let [interestSlabSplitSliderMarks, setInterestSlabSplitSliderMarks] =
    useState(null);
  let [activeSliderMarkIndex, setActiveSliderMarkIndex] = useState(0);
  const [discountBenefitForPaymentPeriod, setDiscountBenefitForPaymentPeriod] =
    useState(null);
  const [sliderValue, setSliderValue] = useState(0);
  const [dayWiseBenefitsData, setDayWiseBenefitsData] = useState(null);

  const firstSlab = modifiedDiscountBenefits?.[0];
  const lastGracePeriodSlabIndex = _.findLastIndex(
    modifiedDiscountBenefits,
    (item) => item?.benefitAmount >= 0
  );
  const lastGracePeriodSlab =
    modifiedDiscountBenefits?.[lastGracePeriodSlabIndex];
  const slabBeforeLastGracePeriodSlab =
    modifiedDiscountBenefits?.[lastGracePeriodSlabIndex - 1];

  const firstInterestSlabIndex = modifiedDiscountBenefits?.findIndex(
    (item) => item?.benefitAmount < 0
  );
  const firstInterestSlab = modifiedDiscountBenefits?.[firstInterestSlabIndex];

  const firstInterestSlabStartingDayNumber = getPreviousBenefitSlabEndDay(
    firstInterestSlab,
    modifiedDiscountBenefits
  );

  const sliderMin = parseDaysFromPaymentPeriodEndDay(
    firstSlab?.paymentPeriodEndDay
  );
  const sliderMax = parseDaysFromPaymentPeriodEndDay(
    firstInterestSlab?.paymentPeriodEndDay
  );

  function parseDaysFromPaymentPeriodEndDay(paymentPeriodEndDay) {
    // accepts strings like "Advance", "2 Days", ">2 days" & returns the parsed day number
    if (paymentPeriodEndDay === "Advance") {
      return 0;
    }
    const match = paymentPeriodEndDay?.match(/\d+/);
    if (match) {
      return parseInt(match[0], 10);
    }
    return null;
  }

  const getPositiveDiscountBenefitsWithFirstNegativeBenefit = (
    discountBenefits
  ) => {
    let positiveDiscountBenefits = [];
    let negativeDiscountBenefits = [];
    let firstNegativeBenefit = null;
    _.forEach(discountBenefits, (benefit) => {
      if (benefit?.benefitAmount >= 0) {
        positiveDiscountBenefits.push(benefit);
      } else {
        negativeDiscountBenefits.push(benefit);
      }
    });
    if (negativeDiscountBenefits.length > 0) {
      firstNegativeBenefit = negativeDiscountBenefits[0];
    }

    return _.concat(positiveDiscountBenefits, firstNegativeBenefit);
  };

  const showSliderLabelForBenefit = (benefit) => {
    // checks whether to display the slider label for a particular benefit slab or not
    if (
      benefit?.id === firstSlab?.id ||
      benefit?.id === lastGracePeriodSlab?.id ||
      benefit?.id === slabBeforeLastGracePeriodSlab?.id
    ) {
      return true;
    }
    return false;
  };

  const showSliderMarkForBenefit = (benefitSlabId) => {
    // checks whether to display the slider mark for a particular benefit slab or not
    const concernedSlabIds = [
      firstSlab?.id,
      // lastGracePeriodSlab?.id,
      slabBeforeLastGracePeriodSlab?.id,
      firstInterestSlab?.id,
    ];
    if (_.includes(concernedSlabIds, benefitSlabId)) {
      return true;
    }
    return false;
  };

  const generateSliderMarks = (discountBenefits) => {
    let generatedSliderMarks = [];
    _.forEach(discountBenefits, (benefit, index) => {
      let sliderMarkItem = {};
      if (benefit?.paymentPeriod) {
        if (
          benefit?.paymentPeriodEndDay &&
          showSliderLabelForBenefit(benefit)
        ) {
          let paymentPeriodDays = 0;
          if (benefit?.paymentPeriodEndDay?.includes(">")) {
            paymentPeriodDays =
              benefit?.paymentPeriodEndDay?.split("days")?.[0];
          } else if (benefit?.paymentPeriodEndDay?.split("Days")) {
            paymentPeriodDays =
              benefit?.paymentPeriodEndDay?.split("Days")?.[0];
          } else {
            paymentPeriodDays = benefit?.paymentPeriodEndDay;
          }
          sliderMarkItem.label = paymentPeriodDays;
        }

        sliderMarkItem.value = parseDaysFromPaymentPeriodEndDay(
          benefit?.paymentPeriodEndDay
        );
        sliderMarkItem.paymentPeriod = benefit?.paymentPeriod;
        sliderMarkItem.paymentPeriodEndDay = benefit?.paymentPeriodEndDay;
        sliderMarkItem.effectiveOfferPrice = benefit?.effectiveOfferPrice;
        sliderMarkItem.benefitType = benefit?.benefitType;
        sliderMarkItem.benefitAmount = benefit?.benefitAmount;
        sliderMarkItem.effectiveTotalNetPrice = benefit?.effectiveTotalNetPrice;
        sliderMarkItem.benefitText = benefit?.benefitText;
        sliderMarkItem.totalCalculatedBenefitAmount =
          benefit?.totalCalculatedBenefitAmount;
        sliderMarkItem.perUnitCalculatedBenefitAmount =
          ProductService.roundFloatsToNdecimalPlacesWithoutFlooring(
            benefit?.perUnitCalculatedBenefitAmount,
            2
          );
        generatedSliderMarks.push(sliderMarkItem);
      }
    });
    return generatedSliderMarks;
  };

  function modifyLastSlabData(data, extendBy) {
    // Modifies the last slab's data, such that payment period is changed from ">x days" to "x to (x+extendBy) Days"
    // Ex: if extendBy=29, then paymentPeriod: ">51 days" becomes "51-80 Days"
    const benefitsData = _.cloneDeep(data);
    const lastObject = benefitsData[benefitsData?.length - 1];
    const lastPaymentPeriod = lastObject.paymentPeriod;

    const numericPart = parseInt(lastPaymentPeriod.match(/\d+/)[0]);

    let newPaymentPeriod = `${numericPart}-${numericPart + extendBy} Days`;
    let newPaymentPeriodEndDay = `${numericPart + extendBy} Days`;

    lastObject.paymentPeriod = newPaymentPeriod;
    lastObject.paymentPeriodEndDay = newPaymentPeriodEndDay;
    lastObject.value = parseDaysFromPaymentPeriodEndDay(
      lastObject?.paymentPeriodEndDay
    );

    return benefitsData;
  }

  useEffect(() => {
    let discountBenefitsFromAppliedOffer = null;
    let sliderMarksFromAppliedBenefits = null;

    if (props.appliedOffer?.offerId && props.cashDiscountBenefits) {
      if (props.cashDiscountBenefits[props.appliedOffer.offerId]) {
        discountBenefitsFromAppliedOffer =
          props.cashDiscountBenefits[props.appliedOffer.offerId];
      } else {
        if (props.cashDiscountBenefits[`no-offer`]) {
          discountBenefitsFromAppliedOffer =
            props.cashDiscountBenefits[`no-offer`];
        } else {
        }
      }
    } else {
      if (props.cashDiscountBenefits) {
        if (props.cashDiscountBenefits[`no-offer`]) {
          discountBenefitsFromAppliedOffer =
            props.cashDiscountBenefits[`no-offer`];
        } else {
        }
      } else {
      }
    }
    if (discountBenefitsFromAppliedOffer) {
      setDiscountBenefits(discountBenefitsFromAppliedOffer);

      const modifiedDiscountBenefitsData = modifyLastSlabData(
        discountBenefitsFromAppliedOffer,
        29
      );
      setModifiedDiscountBenefits(modifiedDiscountBenefitsData);

      let positiveDiscountBenefitsWithFirstNegativeBenefit =
        getPositiveDiscountBenefitsWithFirstNegativeBenefit(
          modifiedDiscountBenefitsData
        );

      sliderMarksFromAppliedBenefits = generateSliderMarks(
        positiveDiscountBenefitsWithFirstNegativeBenefit
      );

      setSliderMarks(sliderMarksFromAppliedBenefits);

      const sliderMarksSplitBasedOnInterestSlab = splitObjectByPaymentPeriod(
        sliderMarksFromAppliedBenefits,
        firstInterestSlabStartingDayNumber + 1
      );
      setInterestSlabSplitSliderMarks(sliderMarksSplitBasedOnInterestSlab);
      setDiscountBenefitForPaymentPeriod(dayWiseBenefitsData?.[sliderValue]);
      props.setAppliedCashDiscountBenefit(dayWiseBenefitsData?.[sliderValue]);
    }
  }, [
    props.appliedOffer?.offerId,
    props.cashDiscountBenefits,
    dayWiseBenefitsData,
  ]);

  const handleSliderChange = (event, newValue, activeThumb) => {
    setSliderValue(newValue);

    let newDiscountBenefitIndex = _.findIndex(
      sliderMarks,
      (mark, index) => {
        return mark.value >= newValue;
      },
      0
    );

    if (newDiscountBenefitIndex !== -1) {
      setDiscountBenefitForPaymentPeriod(dayWiseBenefitsData?.[newValue]);
      props.setAppliedCashDiscountBenefit(dayWiseBenefitsData?.[newValue]);
      setActiveSliderMarkIndex(newDiscountBenefitIndex);
    }
  };

  const handleSliderChangeCommitted = (event, newValue) => {
    AnalyticsService.pushEvent("Cash Discount Benefit Slider Slab Changed", {
      paymentPeriod: dayWiseBenefitsData?.[newValue]?.day + " days",
    });
  };

  useEffect(() => {
    const dayWiseData = [];
    for (let i = 0; i <= sliderMax; i++) {
      const benefitOnNthDay = getDiscountBenefitByDay(i, discountBenefits);

      const updatedBenefitOnNthDay = _.cloneDeep(benefitOnNthDay);
      if (updatedBenefitOnNthDay) {
        updatedBenefitOnNthDay.day = i;
        const previousBenefitSlabEndDay = getPreviousBenefitSlabEndDay(
          benefitOnNthDay,
          modifiedDiscountBenefits
        );

        let cumulativeDepreciatingFactor;
        if (benefitOnNthDay?.benefitAmount < 0) {
          // Interest Slab
          cumulativeDepreciatingFactor =
            benefitOnNthDay?.depreciatingFactor *
            Math.abs(i - previousBenefitSlabEndDay);
        } else {
          cumulativeDepreciatingFactor =
            benefitOnNthDay?.depreciatingFactor *
            Math.abs(i - previousBenefitSlabEndDay - 1);
        }
        updatedBenefitOnNthDay.cumulativeDepreciatingFactor =
          cumulativeDepreciatingFactor;

        let updatedBenefitAmount;
        if (benefitOnNthDay?.benefitAmount < 0) {
          // Interest Slab
          updatedBenefitAmount = cumulativeDepreciatingFactor;
        } else {
          updatedBenefitAmount =
            benefitOnNthDay?.benefitAmount -
            Math.abs(cumulativeDepreciatingFactor);
        }
        updatedBenefitOnNthDay.benefitAmount =
          ProductService.roundFloatsToNdecimalPlacesWithoutFlooring(
            updatedBenefitAmount,
            2
          );
        updatedBenefitOnNthDay.benefitText =
          formatBenefitAmount(updatedBenefitAmount);

        let updatedPerUnitCalculatedBenefitAmount =
          getPerUnitCdOrInterestForDay(
            i,
            benefitOnNthDay,
            previousBenefitSlabEndDay
          );
        updatedPerUnitCalculatedBenefitAmount =
          ProductService.roundFloatsToNdecimalPlacesWithoutFlooring(
            updatedPerUnitCalculatedBenefitAmount,
            2
          );

        updatedBenefitOnNthDay.perUnitCalculatedBenefitAmount =
          updatedPerUnitCalculatedBenefitAmount;

        const totalProductQuantityWithFreeProducts =
          props?.product?.productQuantity +
          (props?.appliedOffer?.freeProductQuantity > 0
            ? props?.appliedOffer?.freeProductQuantity
            : 0);

        updatedBenefitOnNthDay.totalCalculatedBenefitAmount =
          ProductService.roundFloatsToNdecimalPlacesWithoutFlooring(
            totalProductQuantityWithFreeProducts *
              updatedPerUnitCalculatedBenefitAmount,
            2
          );

        const updatedEffectiveOfferPrice =
          ProductService.roundFloatsToNdecimalPlacesWithoutFlooring(
            benefitOnNthDay?.effectiveOfferPrice +
              (benefitOnNthDay?.perUnitCalculatedBenefitAmount -
                updatedPerUnitCalculatedBenefitAmount),
            2
          );

        updatedBenefitOnNthDay.effectiveOfferPrice = updatedEffectiveOfferPrice;
        updatedBenefitOnNthDay.effectiveTotalNetPrice =
          ProductService.roundFloatsToNdecimalPlacesWithoutFlooring(
            totalProductQuantityWithFreeProducts * updatedEffectiveOfferPrice,
            2
          );

        dayWiseData.push(updatedBenefitOnNthDay);
      } else {
        // handling for invalid cases such as Advance, 1-10 Days, 13-20 Days
        // (where CD for an intermediate day is not defined)
        dayWiseData.push(dayWiseData?.[i - 1]);
      }
    }
    setDayWiseBenefitsData(dayWiseData);
  }, [
    sliderMax,
    props?.product?.productQuantity,
    props?.appliedOffer?.freeProductQuantity,
  ]);

  function formatBenefitAmount(benefitAmount) {
    return `${benefitAmount?.toFixed(2)} %`;
  }

  function getDiscountBenefitByDay(dayNumber, discountBenefits) {
    // returns the benefit on the "dayNumber" from original "discountBenefits" list
    if (discountBenefits) {
      for (let i = 0; i < discountBenefits.length; i++) {
        const benefit = discountBenefits[i];
        const periodString = benefit?.paymentPeriod.trim();

        // Handle "Advance" case
        if (periodString.toLowerCase() === "advance") {
          // Check the next benefit's period to determine the end of the "advance" period
          const nextBenefit = discountBenefits?.[i + 1];
          if (nextBenefit) {
            const nextPeriodString = nextBenefit?.paymentPeriod.trim();

            if (nextPeriodString.includes("-")) {
              const [start] = nextPeriodString
                .split("-")
                .map((str) => parseInt(str.replace(/\D/g, "")));
              if (dayNumber < start) {
                return benefit;
              }
            } else if (nextPeriodString.startsWith(">")) {
              const startDay = parseInt(nextPeriodString.replace(/\D/g, ""));
              if (dayNumber < startDay) {
                return benefit;
              }
            } else {
              const exactDay = parseInt(nextPeriodString.replace(/\D/g, ""));
              if (dayNumber < exactDay) {
                return benefit;
              }
            }
          } else {
            // If no next period, treat "advance" as covering all remaining days
            return benefit;
          }
        }

        // Handle open-ended range (e.g., ">150 days")
        if (periodString.startsWith(">")) {
          const startDay = parseInt(periodString.replace(/\D/g, ""));
          if (dayNumber > startDay) {
            return benefit;
          }
        }

        // Handle range-based periods (e.g., "1-30 Days")
        if (periodString.includes("-")) {
          const [start, end] = periodString
            .split("-")
            .map((str) => parseInt(str.replace(/\D/g, "")));
          if (dayNumber >= start && dayNumber <= end) {
            return benefit;
          }
        }

        // Handle exact day number (e.g., "30 Days")
        if (!periodString.includes("-")) {
          const exactDay = parseInt(periodString.replace(/\D/g, ""));
          if (dayNumber === exactDay) {
            return benefit;
          }
        }
      }
    }

    return null;
  }

  function getPerUnitCdOrInterestForDay(
    dayNumber,
    benefit,
    previousBenefitSlabEndDay
  ) {
    if (
      benefit?.benefitType === "PERCENTAGE" ||
      benefit?.benefitType === "ABSOLUTE"
    ) {
      let currentCdOrInterestPercentage = benefit?.benefitAmount;
      let perUnitCalculatedBenefitAmount =
        benefit?.perUnitCalculatedBenefitAmount;
      let DA = benefit?.depreciatingAmount;

      let depreciatingFactorCdOrInterest;
      if (currentCdOrInterestPercentage < 0) {
        // Interest case
        depreciatingFactorCdOrInterest =
          (dayNumber - previousBenefitSlabEndDay) * DA;
      } else {
        // Cash Discount case
        depreciatingFactorCdOrInterest =
          perUnitCalculatedBenefitAmount -
          (dayNumber - previousBenefitSlabEndDay - 1) * DA;
      }

      return depreciatingFactorCdOrInterest;
    } else {
      // only 2 types of CD entitlements are currently supported
      return null;
    }
  }

  function getPreviousBenefitSlabEndDay(benefit, discountBenefits) {
    let benefitId = benefit?.id;
    let previousSlab = _.find(discountBenefits, (discountBenefit) => {
      if (discountBenefit?.id === benefitId - 1) {
        return discountBenefit;
      }
    });

    let currentSlabStartDay = getPaymentPeriodStartDay(benefit);
    let previousBenefitSlabEndDay = parseDaysFromPaymentPeriodEndDay(
      previousSlab?.paymentPeriodEndDay
    );

    if (currentSlabStartDay - previousBenefitSlabEndDay > 1) {
      // cases like "Advance", "5-10 Days",
      return currentSlabStartDay - 1;
    } else {
      return previousBenefitSlabEndDay;
    }
  }

  function getPaymentPeriodStartDay(benefit) {
    if (benefit) {
      if (benefit?.paymentPeriod === "Advance") {
        return 0;
      } else if (benefit?.paymentPeriod?.startsWith(">")) {
        return parseInt(
          benefit?.paymentPeriod.replace(">", "").replace(" days", "").trim(),
          10
        );
      } else {
        const startDay = benefit?.paymentPeriod?.split("-")[0].trim();
        return parseInt(startDay, 10);
      }
    } else {
      return null;
    }
  }

  const checkWhetherToShow = () => {
    let showFlag = true;
    if (props.appliedOffer?.offerId) {
      if (props?.cashDiscountBenefits?.[props.appliedOffer.offerId]) {
        if (
          props.cashDiscountBenefits[props.appliedOffer.offerId].length === 1 &&
          props.cashDiscountBenefits[props.appliedOffer.offerId][0]
            ?.benefitAmount === 0
        ) {
          showFlag = false;
        }
      }
    } else {
      if (props?.cashDiscountBenefits?.["no-offer"]) {
        if (
          props.cashDiscountBenefits["no-offer"].length === 1 &&
          props.cashDiscountBenefits["no-offer"][0]?.benefitAmount === 0
        ) {
          showFlag = false;
        }
      }
    }
    if (props?.isPayAdvanceSchemeActive) {
      showFlag = false;
    }
    return showFlag;
  };

  function whetherToShowNegativeThemeColor() {
    return (
      _.includes(props.appliedCashDiscountBenefit?.benefitText, `-`) ||
      props.appliedCashDiscountBenefit?.benefitAmount === 0
    );
  }
  function getColor() {
    return whetherToShowNegativeThemeColor() ? "#9D2123" : "#00733E";
  }

  function getColorInverted() {
    return whetherToShowNegativeThemeColor() ? "#00733E" : "#9D2123";
  }

  function getMuiColor() {
    return whetherToShowNegativeThemeColor() ? "primary" : "secondary";
  }

  const formatValueLabel = (value) => {
    if (value === 0) {
      return `Advance`;
    } else if (value === 1) {
      return `${value} Day`;
    } else {
      return `${value} Days`;
    }
  };

  const getSliderRailColor = () => {
    const percentageOfSliderRailInSecondaryColor =
      (parseDaysFromPaymentPeriodEndDay(
        slabBeforeLastGracePeriodSlab?.paymentPeriodEndDay
      ) /
        sliderMax) *
      100;

    return `linear-gradient(to right, #00733E ${percentageOfSliderRailInSecondaryColor}%, #9D2123 0%)`;
  };

  function splitObjectByPaymentPeriod(arr, splitDay) {
    // takes the last object in the array, splits it into 2 objects by "splitDay"
    // resultant array has last 2 objects with payment period as "x to splitDay" and "splitDay+1 to y" respectively
    const clonedArr = _.cloneDeep(arr);
    const lastObj = clonedArr?.[clonedArr?.length - 1];
    const [startDay, endDay] = lastObj?.paymentPeriod
      ?.match(/\d+/g)
      ?.map(Number);

    if (splitDay < startDay || splitDay >= endDay) {
      return null;
    }

    const firstObj = _.cloneDeep({
      ...lastObj,
      value: splitDay,
      label: `>${splitDay - 1}`,
      paymentPeriod: `${startDay}-${splitDay} Days`,
      paymentPeriodEndDay: `${splitDay} Days`,
    });
    const secondObj = _.cloneDeep({
      ...lastObj,
      value: endDay,
      paymentPeriod: `${splitDay + 1}-${endDay} Days`,
      paymentPeriodEndDay: `${endDay} Days`,
    });

    clonedArr[clonedArr?.length - 1] = firstObj;
    clonedArr.push(secondObj);
    return clonedArr;
  }

  return (
    <ErrorBoundary message={``}>
      <>
        {checkWhetherToShow() && sliderMarks && (
          <Container maxWidth={`xl`} sx={{ marginTop: "30px" }}>
            <SectionTitle title={`Cash Discount Benefit`} />
            {props.offersLoading !== "started" ? (
              <Card
                sx={{
                  marginTop: "10px",
                  boxShadow: "0px 10px 10px #E0E0E0",
                  borderRadius: "10px",
                }}
              >
                <CardContent sx={{ padding: "40px 17px 17px 17px" }}>
                  <Grid
                    container
                    justifyContent={`center`}
                    alignItems={`center`}
                  >
                    <Grid item xs={11}>
                      {sliderMarks?.length > 0 && (
                        <Slider
                          aria-label="Custom marks"
                          disabled={dayWiseBenefitsData?.length <= 1}
                          value={sliderValue}
                          step={1}
                          // marks={sliderMarks}
                          marks={interestSlabSplitSliderMarks}
                          min={sliderMin}
                          max={sliderMax}
                          onChange={handleSliderChange}
                          onChangeCommitted={handleSliderChangeCommitted}
                          sx={{
                            marginTop: "0px",
                            "& .MuiSlider-markLabel": {
                              fontSize: "11px",
                              fontWeight: "700",
                              "&[data-index]": () => {
                                const styles = {};
                                sliderMarks.forEach((_, index) => {
                                  if (index === lastGracePeriodSlabIndex) {
                                    styles[`&[data-index="${index}"]`] = {
                                      display: "none",
                                    };
                                  }
                                });
                                return styles;
                              },
                            },
                            "& .MuiSlider-markLabelActive": {
                              fontSize: "11px",
                              fontWeight: "700",
                              color: getColor(),
                            },
                            "& .MuiSlider-thumb": {},
                            "& .MuiSlider-mark": {
                              height: "15px",
                              "&[data-index]": () => {
                                const styles = {};
                                for (
                                  let index = 0;
                                  index <= sliderMarks?.length;
                                  index++
                                ) {
                                  if (index === sliderMarks?.length) {
                                    // for the last mark on the slider
                                    styles[`&[data-index="${index}"]`] = {
                                      backgroundColor: "#9D2123",
                                    };
                                  } else if (!showSliderMarkForBenefit(index)) {
                                    styles[`&[data-index="${index}"]`] = {
                                      display: "none",
                                    };
                                  } else {
                                    styles[`&[data-index="${index}"]`] = {
                                      backgroundColor:
                                        index === sliderMarks?.length - 1 ||
                                        index === sliderMarks?.length - 2
                                          ? "#9D2123"
                                          : "#00733E",
                                    };
                                  }
                                }
                                return styles;
                              },
                            },

                            "& .MuiSlider-markActive": {
                              height: "15px",
                              color: getColorInverted(),
                              backgroundColor: getColor(),
                            },
                            "& .MuiSlider-valueLabel": {
                              background: getColor(),
                              borderRadius: "10px",
                              padding: "4px 6px",
                              fontSize: "12px",
                            },
                            "& .MuiSlider-rail": {
                              height: 5,
                              background: getSliderRailColor(),
                            },
                          }}
                          valueLabelDisplay="on"
                          valueLabelFormat={formatValueLabel}
                          track={`normal`}
                          size={`medium`}
                          color={getMuiColor()}
                        />
                      )}
                    </Grid>
                  </Grid>

                  {dayWiseBenefitsData?.[sliderValue] && (
                    <Grid
                      container
                      maxWidth={`xl`}
                      alignItems={`center`}
                      justifyContent={`center`}
                    >
                      <Grid item xs={4}>
                        <Typography
                          variant={`body1`}
                          sx={{ fontWeight: "700" }}
                          align={"center"}
                        >
                          {dayWiseBenefitsData?.[sliderValue]?.day === 0
                            ? "Advance"
                            : dayWiseBenefitsData?.[sliderValue]?.day === 1
                              ? `${dayWiseBenefitsData?.[sliderValue]?.day} Day`
                              : `${dayWiseBenefitsData?.[sliderValue]?.day} Days`}
                        </Typography>
                        <Typography
                          variant={`body2`}
                          sx={{
                            fontSize: "12px",
                            opacity: "0.5",
                            fontWeight: "400",
                          }}
                          align={"center"}
                        >
                          Payment Period
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography
                          variant={`body1`}
                          sx={{
                            fontWeight: "700",
                            filter: PartnerService.applySmudgedPriceFilter(
                              props?.product
                            ),
                          }}
                          align={"center"}
                        >
                          {_.includes(
                            dayWiseBenefitsData?.[sliderValue]?.benefitText,
                            `-`
                          ) ? (
                            <Typography
                              sx={{
                                color: "#9D2123",
                                fontWeight: "700",
                              }}
                            >
                              {dayWiseBenefitsData?.[sliderValue]
                                ?.benefitType === "ABSOLUTE" &&
                                `₹${
                                  _.toString(
                                    dayWiseBenefitsData?.[sliderValue]
                                      ?.totalCalculatedBenefitAmount
                                  ).split(`-`)[1]
                                }`}
                              {dayWiseBenefitsData?.[sliderValue]
                                ?.benefitType === "PERCENTAGE" &&
                                `${
                                  _.toString(
                                    dayWiseBenefitsData?.[sliderValue]
                                      ?.benefitAmount
                                  ).split(`-`)[1]
                                }%`}
                            </Typography>
                          ) : (
                            <>
                              {dayWiseBenefitsData?.[sliderValue]
                                ?.benefitType === "ABSOLUTE" &&
                                `₹${new Intl.NumberFormat("en-IN").format(
                                  dayWiseBenefitsData?.[sliderValue]
                                    ?.totalCalculatedBenefitAmount
                                )}`}
                              {dayWiseBenefitsData?.[sliderValue]
                                ?.benefitType === "PERCENTAGE" &&
                                `${dayWiseBenefitsData?.[sliderValue]?.benefitAmount}%`}
                            </>
                          )}
                        </Typography>
                        <>
                          {_.includes(
                            dayWiseBenefitsData?.[sliderValue]?.benefitText,
                            `-`
                          ) ? (
                            <Typography
                              variant={`body2`}
                              sx={{
                                color: "#9D2123",
                                fontSize: "12px",
                                filter: PartnerService.applySmudgedPriceFilter(
                                  props?.product
                                ),
                              }}
                              align={`center`}
                            >
                              Interest
                            </Typography>
                          ) : (
                            <Typography
                              variant={`body2`}
                              sx={{
                                fontSize: "12px",
                                opacity: "0.5",
                                fontWeight: "400",
                                filter: PartnerService.applySmudgedPriceFilter(
                                  props?.product
                                ),
                              }}
                              align={`center`}
                            >
                              Discount
                            </Typography>
                          )}
                        </>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>
                          {_.includes(
                            _.toString(
                              dayWiseBenefitsData?.[sliderValue]
                                ?.perUnitCalculatedBenefitAmount
                            ),
                            `-`
                          ) ? (
                            <Typography
                              variant={`body1`}
                              sx={{
                                color: "#9D2123",
                                fontWeight: "700",
                                filter: PartnerService.applySmudgedPriceFilter(
                                  props?.product
                                ),
                              }}
                              align={`center`}
                            >
                              ₹
                              {new Intl.NumberFormat("en-IN").format(
                                _.toString(
                                  dayWiseBenefitsData?.[sliderValue]
                                    ?.perUnitCalculatedBenefitAmount
                                ).split(`-`)[1]
                              )}
                            </Typography>
                          ) : (
                            <Typography
                              variant={`body1`}
                              sx={{
                                fontWeight: "700",
                                filter: PartnerService.applySmudgedPriceFilter(
                                  props?.product
                                ),
                              }}
                              align={`center`}
                            >
                              ₹
                              {dayWiseBenefitsData?.[sliderValue]
                                ?.perUnitCalculatedBenefitAmount
                                ? new Intl.NumberFormat("en-IN").format(
                                    dayWiseBenefitsData?.[sliderValue]
                                      ?.perUnitCalculatedBenefitAmount
                                  )
                                : 0}
                            </Typography>
                          )}
                        </Typography>
                        <>
                          {_.includes(
                            dayWiseBenefitsData?.[sliderValue]?.benefitText,
                            `-`
                          ) ? (
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: "400",
                                color: "#9D2123",
                              }}
                              align={"center"}
                            >
                              Interest/Qty
                            </Typography>
                          ) : (
                            <Typography
                              variant={`body2`}
                              sx={{
                                fontSize: "12px",
                                opacity: "0.5",
                                fontWeight: "400",
                              }}
                              align={"center"}
                            >
                              CD Amount/Qty
                            </Typography>
                          )}
                        </>
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
                <Skeleton variant="rectangular" height={150} />
              </>
            )}
          </Container>
        )}
      </>
    </ErrorBoundary>
  );
};

export default DepreciatingCashDiscountBenefitContainer;
